import React, { useState, useEffect, useCallback } from "react";
import { UserType } from "pages/OrganizationAdmin/Users";
import { Tables, showToast } from "@deeptrue-frontend/ui";
import Menu from "../Menu";
import styled from "styled-components/macro";
import User from "./User";
import { Button, Search } from "@deeptrue-frontend/ui";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { useDebounce } from "hooks/useDebouce";
import ModalWithTitle from "components/Modal/ModalWIthTitle";
import Forms from "components/Forms";
import { FormValues } from "components/Forms/Organization/InviteUser";
import api from "api/api";
import { FormikHelpers } from "formik";
import RouterProvider from "components/RouterProvider/RouterProvider";
import { RouteComponentProps } from "react-router-dom";
import queryString from "query-string";
import { defaultLimit } from "pages/OrganizationAdmin/Users";
import { OptionType } from "components/Fields/Search";
import { useFormatMessage } from "components/IntlProvider";

const { ROUTES, withRouter } = RouterProvider;

const AllUsers = styled.span`
  fontsize: 12px;
  color: gray;
`;

const AddButton = styled(Button)`
  display: flex;
  align-items: center;
  svg {
    margin-right: 0.5rem;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  width: 100%;
  align-items: center;
`;

const { Table, TableBody, TableHeader, TablePagination, TableFooter } = Tables;

const thCells = (t: any) => [
  {
    text: t("organization.columns.name", "Name"),
    orderName: "user__last_name",
    props: {
      minWidth: "160px",
      width: "160px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: "Email",
    orderName: "user__email",
    props: {
      minWidth: "215px",
      width: "215px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.role", "Role"),
    orderName: "organization_role",
    props: {
      minWidth: "85px",
      width: "85px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.groups", "Groups"),
    orderName: "groups",
    props: {
      minWidth: "215px",
      width: "215px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.status", "Status"),
    orderName: "is_active",
    props: {
      minWidth: "100px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.lastSignIn", "Last sign in"),
    props: {
      minWidth: "150px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.resources", "Resources"),
    props: {
      minWidth: "90px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
];

interface Props extends RouteComponentProps {
  usersData: UserType[];
  isLoaded: boolean;
  usersCount: number;
  organizationCodename: string;
  refetchUsers: () => void;
  currentPage: number;
  orderBy?: string;
  sortColumns: (colName: string) => void;
}

const UsersList = ({
  usersData,
  isLoaded,
  usersCount,
  organizationCodename,
  refetchUsers,
  currentPage,
  history,
  orderBy,
  sortColumns,
  location,
}: Props) => {
  const [search, setSearchValue] = useState("");
  const [isModalAddUser, setIsModalAddUser] = useState(false);
  const searchValue = useDebounce(search);
  const parsedSearch = queryString.parse(location.search);
  const t = useFormatMessage();

  useEffect(() => {
    const params = {
      limit: defaultLimit,
      offset: 0,
      search: searchValue,
      ordering: parsedSearch.ordering,
    };
    history.push(
      `${ROUTES.ORGANIZATION.ADMIN.USERS}?${queryString.stringify(params)}`
    );
    // eslint-disable-next-line
  }, [searchValue]);

  const inviteUser = useCallback(
    (values: FormValues, { setErrors }: FormikHelpers<FormValues>) => {
      const { email, organization_role, org_groups } = values;

      const formatedGroups = org_groups
        ? org_groups.map((option: OptionType) => option.value)
        : [];
      return api.resources.organization.users
        .inviteUserToOrganization({
          organizationCodename,
          data: {
            email,
            org_groups: formatedGroups,
            organization_role: Number(organization_role),
          },
        })
        .then(() => {
          setIsModalAddUser(false);
          showToast({
            type: "success",
            title: t("organization.invitationSend", "Invitation send"),
          });
          refetchUsers();
        })
        .catch((err) => {
          setErrors(err.response.data);
        });
    },
    // eslint-disable-next-line
    [organizationCodename, refetchUsers]
  );

  const onChangePage = (page: number) => {
    const params = {
      limit: defaultLimit,
      offset: defaultLimit * page - defaultLimit,
      search: searchValue,
      ordering: parsedSearch.ordering,
    };
    history.push(
      `${ROUTES.ORGANIZATION.ADMIN.USERS}?${queryString.stringify(params)}`
    );
  };

  return (
    <>
      <ModalWithTitle
        open={isModalAddUser}
        onClose={() => setIsModalAddUser(false)}
        title={t("tooltip.organizations.invite", "Invite user")}
      >
        <Forms.InviteUserToOrganization
          onSubmit={inviteUser}
          closeModal={() => setIsModalAddUser(false)}
          organizationCodename={organizationCodename}
        />
      </ModalWithTitle>
      <Menu />
      <Search
        placeholder={t("organization.search", "Search...")}
        onChange={(e) => setSearchValue(e.target.value as string)}
      />

      <HeaderWrapper>
        <AllUsers>
          {usersCount}{" "}
          {t("organization.withinOrganization", "{name} withing organization", {
            name: t("organization.users", "users"),
          })}
        </AllUsers>
        <AddButton variant="primary" onClick={() => setIsModalAddUser(true)}>
          <PersonAddIcon />
          <span>{t("tooltip.organizations.invite", "Invite user")}</span>
        </AddButton>
      </HeaderWrapper>
      <Table>
        <TableHeader
          cells={thCells(t)}
          orderBy={orderBy}
          onOrdering={sortColumns}
        />
        <TableBody
          withLoader
          isLoading={!isLoaded}
          isData={usersCount > 0}
          noDataText={t(
            "noresults",
            "No results. We have nothing to show here."
          )}
        >
          {usersData.map((userData: UserType, index: number) => {
            return (
              <User
                userData={userData}
                key={index}
                organizationCodename={organizationCodename}
                refetchUsers={refetchUsers}
              />
            );
          })}
        </TableBody>

        <TableFooter>
          <TablePagination
            count={usersCount}
            currentPage={currentPage}
            rowsPerPage={defaultLimit}
            onChangePage={onChangePage}
          />
        </TableFooter>
      </Table>
    </>
  );
};

export default withRouter(UsersList);
